import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { CampaignControl, EmailPreviews, ProspectStats, VariantTable } from "./ProspectsPanels";

const STATUS = { new: "À contacter", sent: "Email envoyé", followup: "Relancé", replied: "A répondu", interested: "Intéressé", info: "Demande d'infos", enrolled: "Inscrit", refused: "Refus", bounced: "Rejeté", optout: "Désinscrit" };
const EMAIL = { verified: "Vérifiée", unverified: "Non vérifiée", invalid: "Invalide" };
const EMAIL_CLS = { verified: "text-emerald-300", unverified: "text-amber-300", invalid: "text-red-300" };
const sel = "h-10 rounded-sm border border-[var(--line)] bg-[var(--ink)] px-2 text-sm";

function Draft({ p }) {
  const open = async (kind) => {
    try {
      const d = (await api.get(`/admin/prospects/${p.id}/draft/${kind}`)).data;
      await navigator.clipboard?.writeText(d.body).catch(() => {});
      window.location.href = `mailto:${d.to}?subject=${encodeURIComponent(d.subject)}&body=${encodeURIComponent(d.body)}`;
      toast.success("Brouillon ouvert dans votre messagerie (texte aussi copié)");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap gap-1">
      {[["interested", "Réponse intéressé"], ["info", "Réponse infos"], ["followup_info", "Suivi infos"]].map(([k, l]) => (
        <button key={k} data-testid={`prospect-draft-${k}-${p.id}`} onClick={() => open(k)} className="rounded-full border border-[var(--line)] px-2 py-1 text-xs hover:border-[var(--gold)]">{l}</button>
      ))}
    </div>
  );
}

function Row({ p, onSaved }) {
  const [rev, setRev] = useState(p.revenue || 0);
  const save = async (body) => {
    try { await api.patch(`/admin/prospects/${p.id}`, body); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <tr data-testid={`prospect-row-${p.id}`} className="border-t border-[var(--line)] align-top">
      <td className="py-3 pr-3"><b>{p.name}</b><p className="text-xs text-[var(--parchment)]/80">{p.kind === "salon" ? "Salon" : "Créatrice"} · {p.city || "—"} · phase {p.phase}</p><p className="text-xs text-[var(--parchment)]/80">{p.activity}</p></td>
      <td className="pr-3"><a href={`mailto:${p.email}`} className="break-all underline-offset-2 hover:underline">{p.email}</a>
        <p className={`text-xs ${EMAIL_CLS[p.email_status]}`}>{EMAIL[p.email_status]}{p.blocked && p.email_status === "verified" ? ` · ${p.blocked}` : ""}</p>
        <p className="text-xs text-[var(--parchment)]/75">{p.email_check}</p></td>
      <td className="pr-3 text-xs">{p.website ? <a href={p.website} target="_blank" rel="noreferrer" className="block break-all hover:underline">Site</a> : null}{p.instagram ? <a href={p.instagram} target="_blank" rel="noreferrer" className="block hover:underline">Instagram</a> : null}<a href={p.source_url} target="_blank" rel="noreferrer" className="block text-[var(--parchment)]/75 hover:underline">Source</a></td>
      <td className="pr-3">
        <select data-testid={`prospect-status-${p.id}`} value={p.status} onChange={(e) => save({ status: e.target.value })} className={sel}>{Object.entries(STATUS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        {p.email_status === "unverified" ? <button data-testid={`prospect-verify-${p.id}`} onClick={() => save({ email_status: "verified" })} className="mt-1 block text-xs underline">J'ai vérifié cette adresse</button> : null}
        {p.sent_at ? <p className="mt-1 text-xs text-[var(--parchment)]/75">Envoyé {p.sent_at.slice(0, 10)} ({p.sent_variant}){p.followup_at ? ` · relancé ${p.followup_at.slice(0, 10)}` : ""}{p.clicks ? ` · ${p.clicks} clic(s)` : ""}</p> : null}
      </td>
      <td className="pr-3">{p.status === "enrolled" ? <input data-testid={`prospect-revenue-${p.id}`} type="number" min="0" step="0.01" value={rev} onChange={(e) => setRev(e.target.value)} onBlur={() => save({ revenue: Number(rev) || 0 })} className="h-10 w-24 rounded-sm border border-[var(--line)] bg-transparent px-2 text-sm" /> : "—"}</td>
      <td>{["replied", "interested", "info"].includes(p.status) ? <Draft p={p} /> : null}</td>
    </tr>
  );
}

async function exportCsv() {
  try {
    const r = await api.get("/admin/prospects-export.csv", { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = "prospects-ozentya.csv";
    a.click();
  } catch (e) { toast.error(errMsg(e)); }
}

/** Onglet Prospection : base de prospects, campagne en 2 phases, suivi et brouillons de réponse. */
export function ProspectsAdmin() {
  const q = useQuery({ queryKey: ["admin-prospects"], queryFn: async () => (await api.get("/admin/prospects")).data, refetchInterval: 60000 });
  const [kind, setKind] = useState("all");
  const [status, setStatus] = useState("all");
  const [mail, setMail] = useState("all");
  const [search, setSearch] = useState("");
  const rows = useMemo(() => (q.data?.prospects || []).filter((p) => (kind === "all" || p.kind === kind) && (status === "all" || p.status === status)
    && (mail === "all" || p.email_status === mail) && (!search || `${p.name} ${p.city} ${p.email}`.toLowerCase().includes(search.toLowerCase()))), [q.data, kind, status, mail, search]);
  if (q.isLoading) return <Loader2 className="mt-8 h-6 w-6 animate-spin" />;
  if (q.isError) return <p className="mt-8 text-sm">{errMsg(q.error)}</p>;
  const s = q.data?.stats || {};
  return (
    <div data-testid="prospects-admin" className="mt-8 space-y-6">
      <div>
        <h2 className="font-display text-3xl">Prospection formation</h2>
        <p className="mt-1 text-sm text-[var(--parchment)]/85">{s.salons} salons et {s.influence} créatrices trouvés sur des pages publiques · {s.verified} adresses vérifiées, {s.unverified} à vérifier, {s.invalid} invalides (jamais contactées).</p>
      </div>
      <ProspectStats s={s} />
      <CampaignControl campaign={q.data?.campaign} onDone={() => q.refetch()} />
      <EmailPreviews />
      <section className="rounded-sm border border-[var(--line)] p-5"><h3 className="mb-3 font-display text-xl">Tests d'objets</h3><VariantTable variants={s.variants} /></section>
      <section className="rounded-sm border border-[var(--line)] p-5">
        <div className="flex flex-wrap items-center gap-3">
          <select data-testid="prospect-filter-kind" value={kind} onChange={(e) => setKind(e.target.value)} className={sel}><option value="all">Tous</option><option value="salon">Salons</option><option value="influence">Blogueuses / créatrices</option></select>
          <select data-testid="prospect-filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className={sel}><option value="all">Tous les statuts</option>{Object.entries(STATUS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
          <select data-testid="prospect-filter-email" value={mail} onChange={(e) => setMail(e.target.value)} className={sel}><option value="all">Toutes les adresses</option>{Object.entries(EMAIL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
          <input data-testid="prospect-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher" className="h-10 rounded-sm border border-[var(--line)] bg-transparent px-3 text-sm" />
          <button data-testid="prospect-export-btn" onClick={exportCsv} className="inline-flex h-10 items-center gap-2 rounded-full border border-[var(--line)] px-4 text-xs font-semibold uppercase tracking-[0.14em] hover:border-[var(--gold)]"><Download className="h-4 w-4" /> Exporter (CSV)</button>
          <span data-testid="prospect-count" className="text-sm text-[var(--parchment)]/80">{rows.length} affiché(s)</span>
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[920px] text-left text-sm">
            <thead className="text-xs uppercase tracking-[0.12em] text-[var(--parchment)]/75"><tr><th className="py-2">Prospect</th><th>Email</th><th>Liens</th><th>Suivi</th><th>Inscription €</th><th>Réponse</th></tr></thead>
            <tbody>{rows.map((p) => <Row key={p.id} p={p} onSaved={() => q.refetch()} />)}</tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
